import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";

const fetchTeamFiles = async ({ queryKey }) => {
  const [, , teamId, filters] = queryKey;

  const params = new URLSearchParams();
  if (filters?.search) params.append("search", filters.search);
  if (filters?.ordering) params.append("ordering", filters.ordering);
  if (filters?.category) params.append("category", filters.category);
  if (filters?.page) params.append("page", String(filters.page));
  if (filters?.folder_id != null) params.append("folder_id", String(filters.folder_id));
  params.append("page_size", String(filters?.page_size ?? 50));

  const qs = params.toString();
  const url = qs ? endPoints.TeamFilesList(teamId, qs) : endPoints.TeamFilesList(teamId);

  const { data } = await axiosInstance.get(url);
  return data?.data;
};

export const useTeamFiles = (teamId, filters = {}, enabled = true) => {
  const filtersKey = {
    search: filters?.search || "",
    ordering: filters?.ordering || "-created_at",
    category: filters?.category || "",
    page: filters?.page || 1,
    page_size: filters?.page_size || 50,
    folder_id: filters?.folder_id ?? null,
  };

  return useQuery({
    queryKey: queryKeys.TeamFiles(teamId, filtersKey),
    queryFn: fetchTeamFiles,
    enabled: !!teamId && enabled,
    staleTime: 30 * 1000,
  });
};

const postPresignTeamUpload = async ({ teamId, files, folderId }) => {
  const { data } = await axiosInstance.post(
    endPoints.TeamFilesPresign(teamId),
    { files, folder_id: folderId ?? null },
  );
  return data?.data;
};

export const usePresignTeamUpload = () => {
  return useMutation({
    mutationFn: postPresignTeamUpload,
  });
};

const postConfirmTeamUpload = async ({ teamId, payload }) => {
  const { data } = await axiosInstance.post(
    endPoints.TeamFilesConfirm(teamId),
    payload,
  );
  return data?.data;
};

export const useConfirmTeamUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postConfirmTeamUpload,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFilesBase(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFolderTree(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamStorage(variables.teamId),
      });
    },
  });
};

const fetchTeamFileUrl = async ({ teamId, fileId }) => {
  const { data } = await axiosInstance.get(endPoints.TeamFileUrl(teamId, fileId));
  return data?.data?.url;
};

export const useTeamFileUrl = () => {
  return useMutation({
    mutationFn: fetchTeamFileUrl,
  });
};

const deleteTeamFile = async ({ teamId, fileId }) => {
  const { data } = await axiosInstance.delete(endPoints.TeamFileDetail(teamId, fileId));
  return data;
};

export const useDeleteTeamFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTeamFile,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFilesBase(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamStorage(variables.teamId),
      });
    },
  });
};

const patchMoveTeamFile = async ({ teamId, fileId, folderId }) => {
  const { data } = await axiosInstance.patch(
    endPoints.TeamFileDetail(teamId, fileId),
    { folder_id: folderId },
  );
  return data?.data;
};

export const useMoveTeamFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchMoveTeamFile,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFilesBase(variables.teamId),
      });
    },
  });
};

const postCreateTeamShareLink = async ({ teamId, fileId, expiryHours, expiresAt }) => {
  const payload = expiresAt ? { expires_at: expiresAt } : { expires_in_hours: expiryHours ?? 168 };
  const { data } = await axiosInstance.post(
    endPoints.TeamFileShareCreate(teamId, fileId),
    payload,
  );
  return data?.data;
};

export const useCreateTeamShareLink = () => {
  return useMutation({
    mutationFn: postCreateTeamShareLink,
  });
};

const fetchTeamFolderTree = async ({ queryKey }) => {
  const teamId = queryKey[2];
  const { data } = await axiosInstance.get(endPoints.TeamFoldersTree(teamId));
  return data?.data;
};

export const useTeamFolderTree = (teamId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.TeamFolderTree(teamId),
    queryFn: fetchTeamFolderTree,
    enabled: !!teamId && enabled,
    staleTime: 30 * 1000,
  });
};

const postCreateTeamFolder = async ({ teamId, name, parentId }) => {
  const { data } = await axiosInstance.post(
    endPoints.TeamFoldersCreate(teamId),
    { name, parent_id: parentId },
  );
  return data?.data;
};

export const useCreateTeamFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateTeamFolder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFilesBase(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFolderTree(variables.teamId),
      });
    },
  });
};

const patchUpdateTeamFolder = async ({ teamId, folderId, name, parentId }) => {
  const payload = {};
  if (name !== undefined) payload.name = name;
  if (parentId !== undefined) payload.parent_id = parentId;
  const { data } = await axiosInstance.patch(
    endPoints.TeamFolderDetail(teamId, folderId),
    payload,
  );
  return data?.data;
};

export const useUpdateTeamFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchUpdateTeamFolder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFilesBase(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFolderTree(variables.teamId),
      });
    },
  });
};

const deleteTeamFolder = async ({ teamId, folderId }) => {
  const { data } = await axiosInstance.delete(endPoints.TeamFolderDetail(teamId, folderId));
  return data;
};

export const useDeleteTeamFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTeamFolder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFilesBase(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamFolderTree(variables.teamId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.TeamStorage(variables.teamId),
      });
    },
  });
};
